import React, { useState, useEffect, useRef } from 'react';
import { getForexSettings, updateForexSettings, exportExcel, previewImport, commitImport,
         getSnapshots, createSnapshot, restoreSnapshot, deleteSnapshot,
         getTelegramSettings, updateTelegramSettings, getApiKeys, updateApiKeys,
         getProducts, deleteProduct } from '../api/client';

const s = {
  card: { background:'var(--surface,#fff)', border:'1px solid var(--border,#e2e0d8)', borderRadius:12, padding:28, maxWidth:640, marginBottom:24 },
  title: { fontSize:15, fontWeight:700, color:'var(--text,#1a1917)', marginBottom:4, fontFamily:'DM Sans,sans-serif' },
  sub:   { fontSize:12, color:'#9b9890', marginBottom:20, fontFamily:'DM Sans,sans-serif' },
  btn:   { padding:'9px 20px', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'DM Sans,sans-serif', border:'none' },
  btnDark: { background:'#1a1917', color:'#fff' },
  btnOut:  { background:'none', border:'1px solid var(--border,#e2e0d8)', color:'var(--text,#1a1917)' },
  btnRed:  { background:'none', border:'1px solid #fecaca', color:'#dc2626' },
  label: { display:'block', fontSize:11, fontWeight:500, color:'#6b6960', marginBottom:5, textTransform:'uppercase', letterSpacing:.5, fontFamily:'DM Sans,sans-serif' },
  inp:   { padding:'9px 12px', border:'1px solid #e2e0d8', borderRadius:6, fontSize:13, outline:'none', width:'100%', boxSizing:'border-box', fontFamily:'DM Mono,monospace', color:'var(--text,#1a1917)', background:'var(--surface,#fff)' },
  row:   { display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' },
  tag:   { padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:600, fontFamily:'DM Sans,sans-serif' },
};

function Badge({ color, children }) {
  const bg = color==='green'?'#d1fae5':color==='yellow'?'#fef3c7':color==='red'?'#fee2e2':'#f0f0f0';
  const fg = color==='green'?'#065f46':color==='yellow'?'#92400e':color==='red'?'#991b1b':'#6b6960';
  return <span style={{...s.tag, background:bg, color:fg}}>{children}</span>;
}

function Btn({ onClick, disabled, style={}, children }) {
  return <button onClick={onClick} disabled={disabled} style={{...s.btn, ...style, opacity:disabled?.6:1}}>{children}</button>;
}

function timeAgo(iso) {
  const d = (Date.now() - new Date(iso+'Z').getTime()) / 1000;
  if (d < 60) return 'just now';
  if (d < 3600) return Math.floor(d/60) + 'm ago';
  if (d < 86400) return Math.floor(d/3600) + 'h ago';
  return Math.floor(d/86400) + 'd ago';
}

function hoursLeft(iso) {
  const ms = new Date(iso+'Z').getTime() - Date.now();
  if (ms <= 0) return null;
  const h = Math.floor(ms/3600000), m = Math.floor((ms%3600000)/60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// ── Telegram alerts (per-workspace) ─────────────────────────────────────────
function TelegramCard() {
  const [token, setToken] = useState('');
  const [chatId, setChatId] = useState('');
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState(false);

  const load = () => {
    getTelegramSettings()
      .then(r => { setInfo(r.data); setChatId(r.data.telegram_chat_id || ''); })
      .catch(() => setErr('Failed to load'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setErr('');
    setSaving(true);
    try {
      const payload = { telegram_chat_id: chatId };
      if (editing && token) payload.telegram_bot_token = token;
      await updateTelegramSettings(payload);
      setSaved(true); setToken(''); setEditing(false); setTimeout(()=>setSaved(false), 2500);
      setLoading(true); load();
    } catch(e) { setErr(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div style={s.card}>
      <div style={s.title}>Telegram Price Alerts</div>
      <div style={s.sub}>Price drops, stock changes and below-target alerts for this workspace's products go to this bot + chat only.</div>
      {loading ? <div style={{color:'#9b9890', fontSize:13}}>Loading...</div> : (<>
        <div style={{marginBottom:16}}>
          {info?.configured
            ? <Badge color="green">Configured</Badge>
            : <Badge color="red">Not set up — alerts are off</Badge>}
          {info?.using_env_fallback && <span style={{marginLeft:8, fontSize:11, color:'#9b9890', fontFamily:'DM Sans,sans-serif'}}>using shared default bot</span>}
        </div>

        <div style={{marginBottom:14}}>
          <label style={s.label}>Bot Token</label>
          {!editing ? (
            <div style={s.row}>
              <div style={{...s.inp, flex:1, color:'#9b9890'}}>{info?.telegram_bot_token_masked || '— not set —'}</div>
              <Btn onClick={()=>{setEditing(true); setToken('');}} style={s.btnOut}>Change</Btn>
            </div>
          ) : (
            <input value={token} onChange={e=>setToken(e.target.value)} placeholder="123456:ABC-DEF…"
              style={s.inp} autoFocus />
          )}
        </div>

        <div style={{marginBottom:16}}>
          <label style={s.label}>Chat / Group ID</label>
          <input value={chatId} onChange={e=>setChatId(e.target.value)} placeholder="-1001234567890" style={s.inp} />
        </div>

        {err && <div style={{color:'#dc2626', fontSize:12, marginBottom:8}}>{err}</div>}
        <Btn onClick={save} disabled={saving} style={s.btnDark}>{saving?'Saving...':saved?'Saved ✓':'Save Telegram settings'}</Btn>
      </>)}
    </div>
  );
}

// ── API keys (per-workspace override; blank = shared) ───────────────────────
function ApiKeysCard() {
  const [keepa, setKeepa] = useState('');
  const [rainforest, setRainforest] = useState('');
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [editKeepa, setEditKeepa] = useState(false);
  const [editRf, setEditRf] = useState(false);

  const load = () => {
    getApiKeys().then(r => setInfo(r.data)).catch(()=>{}).finally(()=>setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (editKeepa) payload.keepa_api_key = keepa;
      if (editRf) payload.rainforest_api_key = rainforest;
      await updateApiKeys(payload);
      setSaved(true); setEditKeepa(false); setEditRf(false); setKeepa(''); setRainforest('');
      setTimeout(()=>setSaved(false), 2500); setLoading(true); load();
    } catch(e) {} finally { setSaving(false); }
  };

  return (
    <div style={s.card}>
      <div style={s.title}>API Keys &amp; Rate Limits</div>
      <div style={s.sub}>Keepa powers price tracking; Rainforest powers product-name lookup. Leave blank to use the shared account.</div>
      {loading ? <div style={{color:'#9b9890', fontSize:13}}>Loading...</div> : (<>
        <div style={{marginBottom:14}}>
          <label style={s.label}>Keepa API Key {info?.keepa_using_shared && <Badge color="yellow">Shared</Badge>}</label>
          {!editKeepa ? (
            <div style={s.row}>
              <div style={{...s.inp, flex:1, color:'#9b9890'}}>{info?.keepa_api_key ? '•••• (custom)' : 'Using shared account'}</div>
              <Btn onClick={()=>{setEditKeepa(true); setKeepa('');}} style={s.btnOut}>Override</Btn>
            </div>
          ) : (
            <input value={keepa} onChange={e=>setKeepa(e.target.value)} placeholder="Keepa key (blank = shared)" style={s.inp} autoFocus />
          )}
        </div>
        <div style={{marginBottom:16}}>
          <label style={s.label}>Rainforest API Key {info?.rainforest_using_shared && <Badge color="yellow">Shared</Badge>}</label>
          {!editRf ? (
            <div style={s.row}>
              <div style={{...s.inp, flex:1, color:'#9b9890'}}>{info?.rainforest_api_key ? '•••• (custom)' : 'Using shared account'}</div>
              <Btn onClick={()=>{setEditRf(true); setRainforest('');}} style={s.btnOut}>Override</Btn>
            </div>
          ) : (
            <input value={rainforest} onChange={e=>setRainforest(e.target.value)} placeholder="Rainforest key (blank = shared)" style={s.inp} autoFocus />
          )}
        </div>
        <div style={{padding:'10px 14px', background:'#f8f7f4', borderRadius:8, fontSize:11, color:'#6b6960', fontFamily:'DM Sans,sans-serif', marginBottom:16}}>
          {info?.keepa_rate_limit_note}
        </div>
        {(editKeepa || editRf) && <Btn onClick={save} disabled={saving} style={s.btnDark}>{saving?'Saving...':saved?'Saved ✓':'Save keys'}</Btn>}
        {saved && !(editKeepa||editRf) && <span style={{color:'#059669', fontSize:13}}>Saved ✓</span>}
      </>)}
    </div>
  );
}


// ── Remove Products (search by ASIN / name, delete) ─────────────────────────
function RemoveProductsCard() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    setLoading(true);
    getProducts().then(r => setProducts(r.data)).catch(()=>{}).finally(()=>setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const q = query.trim().toLowerCase();
  const matches = q.length < 2 ? [] : products.filter(p =>
    p.asin.toLowerCase().includes(q) || (p.name||'').toLowerCase().includes(q)
  ).slice(0, 20);

  const doDelete = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    try { await deleteProduct(confirmDel.id); setConfirmDel(null); load(); }
    catch(e) { alert(e.response?.data?.detail || 'Delete failed'); }
    finally { setDeleting(false); }
  };

  return (
    <div style={s.card}>
      <div style={s.title}>Remove Products</div>
      <div style={s.sub}>Search by ASIN or name to permanently remove a product from tracking.</div>
      <input value={query} onChange={e=>setQuery(e.target.value)} placeholder="Search ASIN or product name..."
        style={s.inp} />
      {loading ? <div style={{color:'#9b9890', fontSize:13, marginTop:12}}>Loading products...</div> : (
        q.length >= 2 && (
          <div style={{marginTop:14}}>
            {matches.length === 0 && <div style={{fontSize:13, color:'#9b9890', fontFamily:'DM Sans,sans-serif'}}>No matches.</div>}
            {matches.map(p => (
              <div key={p.id} style={{display:'flex', alignItems:'center', gap:12, padding:'10px 14px', background:'#f8f7f4', borderRadius:8, border:'1px solid #e2e0d8', marginBottom:8}}>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontSize:13, fontWeight:600, fontFamily:'DM Sans,sans-serif', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{p.name}</div>
                  <div style={{fontSize:11, color:'#9b9890', fontFamily:'DM Mono,monospace', marginTop:2}}>{p.asin}</div>
                </div>
                <Btn onClick={()=>setConfirmDel(p)} style={{...s.btn, ...s.btnRed, padding:'8px 14px', fontSize:12}}>Remove</Btn>
              </div>
            ))}
          </div>
        )
      )}
      {confirmDel && (
        <div style={{position:'fixed', inset:0, background:'rgba(0,0,0,.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000,cursor:'pointer'}} onClick={()=>!deleting && setConfirmDel(null)}>
          <div style={{background:'var(--surface,#fff)', borderRadius:12, padding:24, maxWidth:400, width:'90%'}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:15, fontWeight:700, marginBottom:8, fontFamily:'DM Sans,sans-serif'}}>Remove this product?</div>
            <div style={{fontSize:13, color:'#6b6960', marginBottom:6, fontFamily:'DM Sans,sans-serif'}}>{confirmDel.name}</div>
            <div style={{fontSize:12, color:'#9b9890', fontFamily:'DM Mono,monospace', marginBottom:20}}>{confirmDel.asin}</div>
            <div style={{fontSize:12, color:'#dc2626', marginBottom:20, fontFamily:'DM Sans,sans-serif'}}>This permanently removes it from tracking and alerts. This cannot be undone from here.</div>
            <div style={{display:'flex', gap:10}}>
              <Btn onClick={doDelete} disabled={deleting} style={{...s.btn, ...s.btnRed, borderColor:'#dc2626'}}>{deleting?'Removing...':'Remove'}</Btn>
              <Btn onClick={()=>setConfirmDel(null)} disabled={deleting} style={s.btnOut}>Cancel</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Settings({ role, exportsEnabled = true }) {
  const isAdmin = role === 'admin';

  // ── Forex ─────────────────────────────────────────────────────────────────
  const [mode, setMode] = useState('live');
  const [manualRate, setManualRate] = useState('');
  const [liveRate, setLiveRate] = useState(null);
  const [fxLoading, setFxLoading] = useState(true);
  const [fxSaving, setFxSaving] = useState(false);
  const [fxSaved, setFxSaved] = useState(false);
  const [fxError, setFxError] = useState('');

  useEffect(() => {
    if (!isAdmin) return;
    getForexSettings()
      .then(r => { setMode(r.data.forex_mode); setManualRate(String(r.data.forex_manual_rate)); setLiveRate(r.data.live_rate); })
      .catch(() => setFxError('Failed to load'))
      .finally(() => setFxLoading(false));
  }, [isAdmin]);

  const saveFx = async () => {
    setFxError('');
    const rate = parseFloat(manualRate);
    if (mode==='manual' && (isNaN(rate)||rate<=0)) { setFxError('Enter a valid positive rate'); return; }
    setFxSaving(true);
    try { await updateForexSettings({forex_mode:mode, forex_manual_rate:rate||undefined}); setFxSaved(true); setTimeout(()=>setFxSaved(false),2500); }
    catch(e) { setFxError(e.response?.data?.detail||'Save failed'); }
    finally { setFxSaving(false); }
  };

  // ── Export ────────────────────────────────────────────────────────────────
  const [exporting, setExporting] = useState('');
  const doExport = async (type) => {
    setExporting(type);
    try {
      const res = await exportExcel(type);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      const cd = res.headers['content-disposition']||'';
      const match = cd.match(/filename="([^"]+)"/);
      a.href = url; a.download = match?match[1]:`adiko_${type}.xlsx`;
      a.click(); URL.revokeObjectURL(url);
    } catch(e) { alert('Export failed'); }
    finally { setExporting(''); }
  };

  // ── Import ────────────────────────────────────────────────────────────────
  const fileRef = useRef();
  const [importFile, setImportFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const doPreview = async () => {
    if (!importFile) return;
    setPreviewing(true); setPreviewData(null); setImportResult(null);
    try { const r = await previewImport(importFile); setPreviewData(r.data); }
    catch(e) { alert(e.response?.data?.detail||'Preview failed'); }
    finally { setPreviewing(false); }
  };

  const doCommit = async () => {
    if (!previewData) return;
    setCommitting(true);
    try {
      const r = await commitImport({updates: previewData.updates, new_products: previewData.new_products});
      setImportResult(r.data); setPreviewData(null); setImportFile(null);
      if (fileRef.current) fileRef.current.value = '';
      loadSnapshots();
    } catch(e) { alert(e.response?.data?.detail||'Import failed'); }
    finally { setCommitting(false); }
  };

  // ── Snapshots ─────────────────────────────────────────────────────────────
  const [snapshots, setSnapshots] = useState([]);
  const [snapsLoading, setSnapsLoading] = useState(false);
  const [newSnapName, setNewSnapName] = useState('');
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [restoreResult, setRestoreResult] = useState(null);

  const loadSnapshots = async () => {
    if (!isAdmin) return;
    setSnapsLoading(true);
    try { const r = await getSnapshots(); setSnapshots(r.data); }
    catch {}
    finally { setSnapsLoading(false); }
  };

  useEffect(() => { loadSnapshots(); }, [isAdmin]);

  const doCreateSnap = async () => {
    if (!newSnapName.trim()) return;
    setCreating(true);
    try { await createSnapshot(newSnapName.trim()); setNewSnapName(''); loadSnapshots(); }
    catch(e) { alert(e.response?.data?.detail||'Failed'); }
    finally { setCreating(false); }
  };

  const doRestore = async (snap) => {
    if (!window.confirm(`Restore to "${snap.name||'auto backup'}"?\n\nThis will revert all product data. Products added after this point will be hidden.`)) return;
    setRestoring(snap.id); setRestoreResult(null);
    try { const r = await restoreSnapshot(snap.id); setRestoreResult(r.data); loadSnapshots(); }
    catch(e) { alert(e.response?.data?.detail||'Restore failed'); }
    finally { setRestoring(null); }
  };

  const doDelete = async (id) => {
    if (!window.confirm('Delete this restore point?')) return;
    try { await deleteSnapshot(id); loadSnapshots(); }
    catch {}
  };

  const autoSnap = snapshots.find(s => s.snapshot_type==='auto' && s.active);
  const namedSnaps = snapshots.filter(s => s.snapshot_type==='named');

  const optStyle = (active) => ({
    display:'flex', alignItems:'flex-start', gap:12, padding:'14px 16px', borderRadius:10,
    border:'2px solid '+(active?'#1a1917':'#e2e0d8'), background:active?'#f8f7f4':'var(--surface,#fff)',
    cursor:'pointer', marginBottom:10,
  });
  const radioStyle = (active) => ({
    width:18, height:18, borderRadius:'50%', border:'2px solid '+(active?'#1a1917':'#cccac0'),
    background:active?'#1a1917':'transparent', flexShrink:0, marginTop:2,
    display:'flex', alignItems:'center', justifyContent:'center',
  });

  return (
    <div style={{padding:'24px 0'}}>
      <div style={{fontSize:22, fontWeight:700, color:'var(--text,#1a1917)', marginBottom:6, fontFamily:'DM Sans,sans-serif'}}>Settings</div>
      <div style={{fontSize:13, color:'#9b9890', marginBottom:28, fontFamily:'DM Sans,sans-serif'}}>{isAdmin?'Admin configuration + data tools':'Data export & import'}</div>

      {/* ── Forex (admin only, exports workspaces only) ── */}
      {isAdmin && exportsEnabled && (
        <div style={s.card}>
          <div style={s.title}>USD / INR Exchange Rate</div>
          <div style={s.sub}>Controls which rate is used across all margin calculations.</div>
          {fxLoading ? <div style={{color:'#9b9890', fontSize:13}}>Loading...</div> : (<>
            {liveRate?.usd_inr && (
              <div style={{marginBottom:20, padding:'12px 16px', background:'#f8f7f4', borderRadius:8, fontSize:12, fontFamily:'DM Sans,sans-serif'}}>
                <span style={{color:'#6b6960'}}>Live API rate: </span>
                <span style={{fontFamily:'DM Mono,monospace', fontWeight:700, color:'#1a1917'}}>₹{liveRate.usd_inr.toFixed(2)}</span>
                {liveRate.age_minutes!=null && <span style={{color:'#9b9890', marginLeft:10}}>· {liveRate.age_minutes}m ago</span>}
              </div>
            )}
            <div onClick={()=>setMode('live')} style={optStyle(mode==='live')}>
              <div style={radioStyle(mode==='live')}>{mode==='live'&&<div style={{width:6,height:6,borderRadius:'50%',background:'#fff'}}/>}</div>
              <div>
                <div style={{fontSize:13, fontWeight:600, fontFamily:'DM Sans,sans-serif'}}>Live API rate <Badge color="green">Recommended</Badge></div>
                <div style={{fontSize:11, color:'#9b9890', marginTop:2, fontFamily:'DM Sans,sans-serif'}}>Fetches from ExchangeRate-API every 30 min.</div>
              </div>
            </div>
            <div onClick={()=>setMode('manual')} style={optStyle(mode==='manual')}>
              <div style={radioStyle(mode==='manual')}>{mode==='manual'&&<div style={{width:6,height:6,borderRadius:'50%',background:'#fff'}}/>}</div>
              <div style={{width:'100%'}}>
                <div style={{fontSize:13, fontWeight:600, fontFamily:'DM Sans,sans-serif'}}>Manual rate <Badge color="yellow">Fixed</Badge></div>
                <div style={{fontSize:11, color:'#9b9890', marginTop:2, fontFamily:'DM Sans,sans-serif'}}>Use a fixed rate for budgeting or when API is unavailable.</div>
                {mode==='manual' && (
                  <div style={{display:'flex', alignItems:'center', gap:8, marginTop:12}}>
                    <span style={{fontFamily:'DM Mono,monospace', fontSize:16, color:'#6b6960'}}>₹</span>
                    <input type="number" step="0.01" min="1" value={manualRate} onChange={e=>setManualRate(e.target.value)}
                      onClick={e=>e.stopPropagation()} placeholder="e.g. 84.50"
                      style={{...s.inp, width:160}} />
                    <span style={{fontSize:12, color:'#9b9890', fontFamily:'DM Sans,sans-serif'}}>per USD</span>
                  </div>
                )}
              </div>
            </div>
            {fxError && <div style={{color:'#dc2626', fontSize:12, marginBottom:8}}>{fxError}</div>}
            <Btn onClick={saveFx} disabled={fxSaving} style={s.btnDark}>{fxSaving?'Saving...':fxSaved?'Saved ✓':'Save settings'}</Btn>
          </>)}
        </div>
      )}

      {/* ── Telegram + API keys (admin only) ── */}
      {isAdmin && <RemoveProductsCard />}

      {/* ── Export ── */}
      <div style={s.card}>
        <div style={s.title}>Export to Excel</div>
        <div style={s.sub}>Downloads a .xlsx file with all current product data. Grey [ref] columns are for reference only — they are ignored during import.</div>
        <div style={s.row}>
          {isAdmin && exportsEnabled && (
            <Btn onClick={()=>doExport('exports')} disabled={exporting==='exports'} style={s.btnDark}>
              {exporting==='exports'?'Exporting...':'↓ Exports Dashboard'}
            </Btn>
          )}
          <Btn onClick={()=>doExport('domestic')} disabled={exporting==='domestic'} style={s.btnDark}>
            {exporting==='domestic'?'Exporting...':'↓ Dashboard'}
          </Btn>
        </div>
      </div>

      {/* ── Import ── */}
      <div style={s.card}>
        <div style={s.title}>Import from Excel</div>
        <div style={s.sub}>
          Upload a previously exported file with your changes. Only ASIN is required — all other fields are optional.
          {!isAdmin && ' You can update: Name, Colour, Target Price.'}
        </div>

        <div style={{display:'flex', gap:10, alignItems:'center', marginBottom:16, flexWrap:'wrap'}}>
          <input ref={fileRef} type="file" accept=".xlsx"
            onChange={e=>{setImportFile(e.target.files[0]||null); setPreviewData(null); setImportResult(null);}}
            style={{fontSize:13, fontFamily:'DM Sans,sans-serif', color:'var(--text,#1a1917)'}} />
          <Btn onClick={doPreview} disabled={!importFile||previewing} style={s.btnDark}>
            {previewing?'Analysing...':'Preview Changes'}
          </Btn>
        </div>

        {/* Preview panel */}
        {previewData && (
          <div style={{border:'1px solid var(--border,#e2e0d8)', borderRadius:10, overflow:'hidden', marginBottom:16}}>
            <div style={{background:'#f8f7f4', padding:'12px 16px', borderBottom:'1px solid #e2e0d8', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
              <div style={{fontWeight:600, fontSize:13, fontFamily:'DM Sans,sans-serif'}}>
                Preview — {previewData.updates.length} update{previewData.updates.length!==1?'s':''}, {previewData.new_products.length} new product{previewData.new_products.length!==1?'s':''}
              </div>
              <button onClick={()=>setPreviewData(null)} style={{background:'none',border:'none',cursor:'pointer',fontSize:16,color:'#9b9890'}}>✕</button>
            </div>

            <div style={{padding:16, maxHeight:360, overflowY:'auto'}}>
              {previewData.errors.length > 0 && (
                <div style={{marginBottom:12}}>
                  <div style={{fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:.5, color:'#dc2626', marginBottom:6}}>Errors</div>
                  {previewData.errors.map((e,i)=><div key={i} style={{fontSize:12, color:'#dc2626', fontFamily:'DM Mono,monospace', marginBottom:3}}>{e}</div>)}
                </div>
              )}
              {previewData.new_products.length > 0 && (
                <div style={{marginBottom:12}}>
                  <div style={{fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:.5, color:'#059669', marginBottom:6}}>New products to add</div>
                  {previewData.new_products.map((p,i)=>(
                    <div key={i} style={{fontSize:12, fontFamily:'DM Mono,monospace', marginBottom:3, color:'var(--text,#1a1917)'}}>
                      {p.asin}{p.domestic_srp?` — Target: ₹${p.domestic_srp}`:''}
                    </div>
                  ))}
                </div>
              )}
              {previewData.updates.length > 0 && (
                <div>
                  <div style={{fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:.5, color:'#1a1917', marginBottom:8}}>Changes</div>
                  <table style={{width:'100%', borderCollapse:'collapse', fontSize:12}}>
                    <thead>
                      <tr style={{background:'#f0efe9'}}>
                        {['Product','Field','Before','After'].map(h=>(
                          <th key={h} style={{padding:'6px 10px', textAlign:'left', fontWeight:600, fontFamily:'DM Sans,sans-serif', fontSize:11, color:'#6b6960', borderBottom:'1px solid #e2e0d8'}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.updates.flatMap(upd =>
                        upd.changes.map((ch,i)=>(
                          <tr key={upd.id+'-'+ch.field} style={{borderBottom:'1px solid rgba(0,0,0,.04)'}}>
                            <td style={{padding:'6px 10px', fontFamily:'DM Sans,sans-serif', color:'#1a1917', maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{i===0?upd.name:''}</td>
                            <td style={{padding:'6px 10px', fontFamily:'DM Sans,sans-serif', color:'#6b6960'}}>{ch.label}</td>
                            <td style={{padding:'6px 10px', fontFamily:'DM Mono,monospace', color:'#9b9890', textDecoration:'line-through'}}>{ch.old??'—'}</td>
                            <td style={{padding:'6px 10px', fontFamily:'DM Mono,monospace', color:'#059669', fontWeight:600}}>{ch.new}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {(previewData.updates.length > 0 || previewData.new_products.length > 0) && (
              <div style={{padding:'12px 16px', borderTop:'1px solid #e2e0d8', display:'flex', gap:10}}>
                <Btn onClick={doCommit} disabled={committing} style={s.btnDark}>
                  {committing?'Importing...':'✓ Confirm Import'}
                </Btn>
                <Btn onClick={()=>setPreviewData(null)} style={s.btnOut}>Cancel</Btn>
              </div>
            )}
          </div>
        )}

        {importResult && (
          <div style={{padding:'12px 16px', background:'#d1fae5', borderRadius:8, fontSize:13, fontFamily:'DM Sans,sans-serif', color:'#065f46'}}>
            ✓ Import complete — {importResult.updated} updated, {importResult.added} added
            {importResult.errors?.length>0 && <div style={{marginTop:4, color:'#dc2626'}}>{importResult.errors.join('; ')}</div>}
          </div>
        )}
      </div>

      {/* ── Restore Points (admin only) ── */}
      {isAdmin && (
        <div style={s.card}>
          <div style={s.title}>Restore Points</div>
          <div style={s.sub}>Revert all product data to a previous state. Products added after the restore point will be hidden (can be re-enabled in Product Master).</div>

          {restoreResult && (
            <div style={{padding:'10px 14px', background:'#d1fae5', borderRadius:8, fontSize:13, fontFamily:'DM Sans,sans-serif', color:'#065f46', marginBottom:16}}>
              ✓ Restored — {restoreResult.updated} products updated, {restoreResult.hidden} hidden (added after snapshot)
            </div>
          )}

          {/* Auto backup */}
          <div style={{marginBottom:20}}>
            <div style={{fontSize:12, fontWeight:600, textTransform:'uppercase', letterSpacing:.5, color:'#6b6960', marginBottom:10, fontFamily:'DM Sans,sans-serif'}}>Auto Backup (before each import)</div>
            {autoSnap ? (
              <div style={{display:'flex', alignItems:'center', gap:12, padding:'12px 16px', background:'#f8f7f4', borderRadius:10, border:'1px solid #e2e0d8'}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:13, fontWeight:600, fontFamily:'DM Sans,sans-serif'}}>Last import backup</div>
                  <div style={{fontSize:11, color:'#9b9890', fontFamily:'DM Mono,monospace', marginTop:2}}>
                    {timeAgo(autoSnap.created_at)} · expires in {hoursLeft(autoSnap.expires_at)||'soon'}
                  </div>
                </div>
                <Btn onClick={()=>doRestore(autoSnap)} disabled={restoring===autoSnap.id} style={{...s.btn,...s.btnOut}}>
                  {restoring===autoSnap.id?'Restoring...':'↩ Undo Last Import'}
                </Btn>
              </div>
            ) : (
              <div style={{fontSize:13, color:'#9b9890', fontFamily:'DM Sans,sans-serif', padding:'12px 0'}}>No import has been done yet, or the 72-hour undo window has passed.</div>
            )}
          </div>

          {/* Named restore points */}
          <div>
            <div style={{fontSize:12, fontWeight:600, textTransform:'uppercase', letterSpacing:.5, color:'#6b6960', marginBottom:10, fontFamily:'DM Sans,sans-serif'}}>Named Restore Points ({namedSnaps.length}/5)</div>
            <div style={{display:'flex', gap:8, marginBottom:16}}>
              <input value={newSnapName} onChange={e=>setNewSnapName(e.target.value)}
                onKeyDown={e=>e.key==='Enter'&&doCreateSnap()}
                placeholder="e.g. Before price update May 19"
                style={{...s.inp, flex:1}} />
              <Btn onClick={doCreateSnap} disabled={creating||!newSnapName.trim()||namedSnaps.length>=5} style={s.btnDark}>
                {creating?'Saving...':'+ Save point'}
              </Btn>
            </div>
            {namedSnaps.length===0 && <div style={{fontSize:13, color:'#9b9890', fontFamily:'DM Sans,sans-serif'}}>No named restore points yet.</div>}
            {namedSnaps.map(snap=>(
              <div key={snap.id} style={{display:'flex', alignItems:'center', gap:12, padding:'12px 16px', background:'#f8f7f4', borderRadius:10, border:'1px solid #e2e0d8', marginBottom:8}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:13, fontWeight:600, fontFamily:'DM Sans,sans-serif'}}>{snap.name}</div>
                  <div style={{fontSize:11, color:'#9b9890', fontFamily:'DM Mono,monospace', marginTop:2}}>{timeAgo(snap.created_at)}</div>
                </div>
                <Btn onClick={()=>doRestore(snap)} disabled={restoring===snap.id} style={{...s.btn,...s.btnOut, fontSize:12}}>
                  {restoring===snap.id?'Restoring...':'↩ Restore'}
                </Btn>
                <Btn onClick={()=>doDelete(snap.id)} style={{...s.btn,...s.btnRed, padding:'9px 12px', fontSize:12}}>✕</Btn>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
